import {
    Button,
    Checkbox,
    Container,
    Divider,
    Dropdown,
    Form,
    FormGroup,
    Input,
    InputPlaceholder,
    Segment,
    SegmentContent,
    SegmentHeader,
    Toggle,
} from '@/platform/ui';
import {
    ArrowLeftIcon,
    EnvelopeIcon,
    LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Deferred, Link, router, useForm, usePage } from '@inertiajs/react';
import { useEffect } from 'react';

const CreateOrUpdateView = () => {
    const { auth, user, roles, customers, resellers } = usePage().props;
    const { data, setData, post, patch, processing, errors } = useForm({});

    useEffect(() => {
        setData(
            user?.id
                ? {
                      ...user,
                      customers: user?.customers?.map(c => c.id),
                      roles: user?.roles?.map(r => r.id),
                  }
                : {
                      is_active: true,
                      reseller_id: 3,
                  }
        );
    }, [user]);

    const sendPassword = () => {
        router.post(
            `/users/${user.id}/send-password`,
            {},
            {
                preserveScroll: true,
                preserveState: true,
            }
        );
    };

    const handleSubmit = () => {
        if (user?.id) {
            patch(`/users/${user.id}`);

            return;
        }

        post(`/users`);
    };

    return (
        <Container>
            <Form onSubmit={handleSubmit} noValidate>
                <Segment>
                    <SegmentHeader
                        header={
                            user?.id
                                ? 'Gebruiker wijzigen'
                                : 'Gebruiker toevoegen'
                        }
                    >
                        {user?.id && (
                            <>
                                {[
                                    'impersonate users',
                                    'impersonate internal users',
                                    'impersonate all users',
                                ]?.some(perm =>
                                    auth?.permissions?.includes(perm)
                                ) && (
                                    <Button
                                        type='button'
                                        onClick={() =>
                                            router.post(
                                                `/impersonate/login/${user?.id}`
                                            )
                                        }
                                    >
                                        <LockClosedIcon className='size-4' />
                                        Inloggen
                                    </Button>
                                )}
                                <Button type='button' onClick={sendPassword}>
                                    <EnvelopeIcon className='size-4' />
                                    Wachtwoordherstel versturen
                                </Button>
                            </>
                        )}
                        <Button as={Link} href='/users'>
                            <ArrowLeftIcon className='size-5' />
                            Terug
                        </Button>
                    </SegmentHeader>
                    <SegmentContent>
                        {auth?.permissions?.includes('view resellers') && (
                            <FormGroup>
                                <Toggle
                                    label='Actief'
                                    checked={data?.is_active}
                                    onChange={checked =>
                                        setData('is_active', checked)
                                    }
                                />
                                {auth?.permissions?.includes(
                                    'assign reseller to user'
                                ) && (
                                    <Deferred
                                        data='resellers'
                                        fallback={
                                            <InputPlaceholder label='Reseller' />
                                        }
                                    >
                                        <Dropdown
                                            label='Reseller'
                                            required
                                            options={resellers || []}
                                            value={data?.reseller_id || 3}
                                            onChange={value =>
                                                setData('reseller_id', value)
                                            }
                                            error={errors?.reseller_id}
                                        />
                                    </Deferred>
                                )}
                            </FormGroup>
                        )}
                        <FormGroup inline equal>
                            <Input
                                label='Voornaam'
                                required
                                value={data?.first_name || ''}
                                onChange={event =>
                                    setData('first_name', event.target.value)
                                }
                                error={errors?.first_name}
                            />
                            <Input
                                label='Tussenvoegsel'
                                value={data?.affix || ''}
                                onChange={event =>
                                    setData('affix', event.target.value)
                                }
                                error={errors?.affix}
                            />
                            <Input
                                label='Achternaam'
                                required
                                value={data?.last_name || ''}
                                onChange={event =>
                                    setData('last_name', event.target.value)
                                }
                                error={errors?.last_name}
                            />
                        </FormGroup>
                        <FormGroup inline equal>
                            <Input
                                label='E-mailadres'
                                required
                                type='email'
                                disabled={user?.id === auth?.user?.id}
                                value={data?.email || ''}
                                onChange={event =>
                                    setData('email', event.target.value)
                                }
                                error={errors?.email}
                            />
                            <Input
                                label='Telefoonnummer'
                                value={data?.telephone_number || ''}
                                onChange={event =>
                                    setData(
                                        'telephone_number',
                                        event.target.value
                                    )
                                }
                                error={errors?.telephone_number}
                            />
                        </FormGroup>
                        <FormGroup>
                            {[
                                'view role options',
                                'view reseller role options',
                                'assign customer to user',
                            ]?.some(perm =>
                                auth?.permissions?.includes(perm)
                            ) && <Divider content='Toegang' />}
                            {[
                                'view role options',
                                'view reseller role options',
                            ]?.some(perm =>
                                auth?.permissions?.includes(perm)
                            ) && (
                                <Deferred
                                    data='roles'
                                    fallback={
                                        <InputPlaceholder label='Rol(len)' />
                                    }
                                >
                                    <Dropdown
                                        label='Rol(len)'
                                        required
                                        multiple
                                        options={roles || []}
                                        value={data?.roles || []}
                                        onChange={value =>
                                            setData('roles', value)
                                        }
                                        error={errors?.roles}
                                    />
                                </Deferred>
                            )}
                            {auth?.permissions?.includes(
                                'assign customer to user'
                            ) && (
                                <Deferred
                                    data='customers'
                                    fallback={
                                        <InputPlaceholder label='Klant(en)' />
                                    }
                                >
                                    <Dropdown
                                        label='Klant(en)'
                                        multiple
                                        options={customers || []}
                                        value={data?.customers || []}
                                        onChange={value =>
                                            setData('customers', value)
                                        }
                                        error={errors?.customers}
                                    />
                                </Deferred>
                            )}

                            {[
                                'update users allowed two factor types',
                                'set user to sso only',
                            ]?.some(perm =>
                                auth?.permissions?.includes(perm)
                            ) && <Divider content='Authenticatie' />}

                            {auth?.permissions?.includes(
                                'update users allowed two factor types'
                            ) && (
                                <Dropdown
                                    multiple
                                    label='Toegestane tweestapsverificatie type'
                                    options={[
                                        {
                                            id: 'email',
                                            name: 'Code per e-mail',
                                        },
                                        {
                                            id: 'google',
                                            name: '(Google) Authenticator (OTP)',
                                        },
                                    ]}
                                    value={data?.allowed_2fa || []}
                                    onChange={value =>
                                        setData('allowed_2fa', value)
                                    }
                                    error={errors?.allowed_2fa}
                                />
                            )}
                            {auth?.permissions?.includes(
                                'set user to sso only'
                            ) && (
                                <Toggle
                                    label='Alleen SSO'
                                    description='Indien ingeschakeld, kan de gebruiker alleen inloggen via SSO. Het wachtwoord zal niet meer werken voor authenticatie.'
                                    checked={data?.sso_only || false}
                                    onChange={checked =>
                                        setData('sso_only', checked)
                                    }
                                />
                            )}

                            <Divider content='Wachtwoord' />

                            <Checkbox
                                label='Verstuur (opnieuw) een e-mail voor wachtwoordherstel naar de gebruiker'
                                checked={!!data?.send_invite || false}
                                onChange={checked =>
                                    setData('send_invite', checked)
                                }
                            />
                        </FormGroup>
                    </SegmentContent>
                </Segment>

                <Segment floating>
                    <SegmentContent right>
                        <Button
                            type='submit'
                            variant='primary'
                            loading={processing}
                        >
                            Opslaan
                        </Button>
                    </SegmentContent>
                </Segment>
            </Form>
        </Container>
    );
};

export default CreateOrUpdateView;
